import { supabase, toError } from './supabase';

export interface ProjectReactivationResult {
    project_id: string;
    visibility: 'public' | 'private' | 'unlisted';
    made_public: boolean;
    already_active: boolean;
    max_projects: number;
    active_projects: number;
}

const PROJECT_PUBLICATION_CONFIRMATION_REQUIRED = 'PROJECT_PUBLICATION_CONFIRMATION_REQUIRED';

type RpcClient = {
    rpc: (
        functionName: string,
        args: Record<string, unknown>,
    ) => Promise<{ data: unknown; error: unknown }>;
};

function parseResult(value: unknown, projectId: string): ProjectReactivationResult {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('Invalid project reactivation response');
    }

    const record = value as Record<string, unknown>;
    const visibility = record.visibility;
    const validVisibility = visibility === 'public' || visibility === 'private' || visibility === 'unlisted';
    const validCounts = Number.isInteger(record.max_projects)
        && Number(record.max_projects) >= 0
        && Number.isInteger(record.active_projects)
        && Number(record.active_projects) >= 0;

    if (
        record.project_id !== projectId
        || !validVisibility
        || typeof record.made_public !== 'boolean'
        || typeof record.already_active !== 'boolean'
        || !validCounts
    ) {
        throw new Error('Invalid project reactivation response');
    }

    return {
        project_id: projectId,
        visibility,
        made_public: record.made_public,
        already_active: record.already_active,
        max_projects: Number(record.max_projects),
        active_projects: Number(record.active_projects),
    };
}

export function isProjectPublicationConfirmationRequiredError(error: unknown): boolean {
    if (!error) return false;
    if (typeof error === 'string') return error.includes(PROJECT_PUBLICATION_CONFIRMATION_REQUIRED);
    if (typeof error !== 'object') return false;

    const record = error as Record<string, unknown>;
    return [record.code, record.message, record.details, record.hint]
        .some((value) => typeof value === 'string' && value.includes(PROJECT_PUBLICATION_CONFIRMATION_REQUIRED));
}

export async function reactivateProjectWithEntitlements(
    projectId: string,
    allowMakePublic = false,
): Promise<ProjectReactivationResult> {
    // The migration is intentionally not represented in the generated client types yet.
    const rpcClient = supabase as unknown as RpcClient;
    const { data, error } = await rpcClient.rpc('reactivate_project_with_entitlements', {
        p_project_id: projectId,
        p_allow_make_public: allowMakePublic,
    });

    if (error) {
        throw toError(error);
    }

    return parseResult(data, projectId);
}
